/**
 * Shared coarse terrain-elevation grid (issue #276, phase 4.2).
 *
 * One `queryTerrainElevation` raycast per grid post, built once per rebuild
 * and bilinearly interpolated everywhere else — used by the 3D trees (plant
 * bases + shadow receiver) and the 3D buildings (bases + receiver), replacing
 * the buildings' ~25k per-vertex raycasts. NaN marks a post whose DEM tile
 * hasn't loaded; callers flag the build incomplete and retry.
 */

import mapboxgl from "mapbox-gl";
import type { Bbox, ElevGridData } from "./types";

export interface ElevGrid extends ElevGridData {
  missing: boolean;
}

const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));

/** Sample terrain once over a mercator bbox into an n×n grid. */
export function buildElevGrid(map: mapboxgl.Map, view: Bbox, n: number): ElevGrid {
  const terrainOn = Boolean(map.getTerrain());
  const x0 = view[0];
  const y0 = view[1];
  const dx = (view[2] - view[0]) / (n - 1) || 1e-9;
  const dy = (view[3] - view[1]) / (n - 1) || 1e-9;
  const vals = new Float32Array(n * n);
  let missing = false;
  if (!terrainOn) {
    // No DEM → flat ground at 0.
    return { x0, y0, dx, dy, n, vals, missing };
  }
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const ll = new mapboxgl.MercatorCoordinate(x0 + i * dx, y0 + j * dy, 0).toLngLat();
      const e = map.queryTerrainElevation(ll, { exaggerated: true });
      if (e == null) {
        missing = true;
        vals[j * n + i] = NaN;
      } else {
        vals[j * n + i] = e;
      }
    }
  }
  return { x0, y0, dx, dy, n, vals, missing };
}

/** Bilinear terrain elevation at a mercator point; NaN if any corner is missing. */
export function elevAt(g: ElevGridData, mx: number, my: number): number {
  const fx = clamp((mx - g.x0) / g.dx, 0, g.n - 1.0001);
  const fy = clamp((my - g.y0) / g.dy, 0, g.n - 1.0001);
  const ix = Math.floor(fx);
  const iy = Math.floor(fy);
  const tx = fx - ix;
  const ty = fy - iy;
  const v = g.vals;
  const a = v[iy * g.n + ix];
  const b = v[iy * g.n + ix + 1];
  const c = v[(iy + 1) * g.n + ix];
  const d = v[(iy + 1) * g.n + ix + 1];
  const top = a + (b - a) * tx;
  const bot = c + (d - c) * tx;
  return top + (bot - top) * ty;
}
